/**
 * BackgroundField — Atmospheric Archive
 * ---------------------------------------------------------------
 * Cold, observant, archival. Matte-black dominant with a slow
 * ambient drift, deep vignette, faint film-grain scanlines, and
 * a restrained tactical bar at the top. No flash, no neon — this
 * is pressure ambience, not decoration.
 */

import { AbsoluteFill, useCurrentFrame, interpolate } from "remotion";
import { PALETTE } from "../utils/timing";

interface Props {
  /** Resonant isolation intensity (0..1) — dims field and softens. */
  isolate?: number;
}

export const BackgroundField: React.FC<Props> = ({ isolate = 0 }) => {
  const frame = useCurrentFrame();

  // Slow drifting radial — much subtler than before
  const drift = interpolate(frame, [0, 1200], [0, 40]);
  const ambientPulse = interpolate(
    frame % 300,
    [0, 150, 300],
    [0.025, 0.06, 0.025]
  );
  // Resonance softens the field even further
  const fieldOpacity = 1 - isolate * 0.35;

  return (
    <AbsoluteFill style={{ backgroundColor: PALETTE.bgDeep }}>
      {/* Slow ambient radial */}
      <AbsoluteFill
        style={{
          opacity: fieldOpacity,
          background: `radial-gradient(circle at ${48 + drift / 8}% ${40 - drift / 12}%, rgba(103,232,249,${ambientPulse}) 0%, transparent 60%)`,
        }}
      />
      {/* Deep cinematic vignette */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(ellipse at 50% 55%, transparent 35%, rgba(0,0,0,0.55) 85%, rgba(0,0,0,0.85) 100%)",
        }}
      />
      {/* Bottom heavy vignette */}
      <AbsoluteFill
        style={{
          background:
            "linear-gradient(to bottom, transparent 55%, rgba(0,0,0,0.6) 100%)",
        }}
      />
      {/* Archival film-grain scanlines */}
      <AbsoluteFill
        style={{
          opacity: 0.55,
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.012) 0 1px, transparent 1px 4px)",
          mixBlendMode: "overlay",
        }}
      />
      {/* Faint horizontal archive band */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 110,
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.55), transparent)",
        }}
      />
      {/* Top tactical bar — restrained */}
      <div
        style={{
          position: "absolute",
          top: 56,
          left: 56,
          right: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 18,
          letterSpacing: "0.42em",
          textTransform: "uppercase",
          color: PALETTE.textDim,
          opacity: 0.7,
        }}
      >
        <span style={{ color: PALETTE.cyanSoft }}>GREYROCK&nbsp;AI®</span>
        <span>FORENSIC&nbsp;//&nbsp;ARCHIVE</span>
      </div>
      {/* Bottom timecode */}
      <div
        style={{
          position: "absolute",
          bottom: 56,
          left: 56,
          right: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 14,
          letterSpacing: "0.5em",
          textTransform: "uppercase",
          color: PALETTE.textDimmer,
          opacity: 0.55,
        }}
      >
        <span>SEQ · {String(frame).padStart(5, "0")}</span>
        <span>OBSERVATIONAL · NOT PREDICTIVE</span>
      </div>
    </AbsoluteFill>
  );
};
